/**
 * Die Liquiditaetsprognose.
 *
 * Hier wird die zentrale Invariante des Systems eingeloest:
 *
 *   Der Ist-Kontostand enthaelt bereits jede gebuchte Banktransaktion.
 *   Ein geplanter Posten zaehlt deshalb nur noch mit seinem offenen
 *   Restbetrag. Ein Match realisiert den Plan — er addiert nichts hinzu.
 *
 * Alles andere in dieser Datei sind Details: wie ueberfaellige Posten
 * fortgeschrieben werden, wie gelerntes Zahlungsverhalten einfliesst und wie
 * Szenarien als Overlay darauf gelegt werden. Die Invariante selbst steht in
 * genau einer Zeile — `signedOpenCents` in `types.ts`.
 *
 * Die Berechnung laeuft bewusst synchron und ohne I/O, damit sie im Browser
 * bei jedem Reglerzug neu ausgefuehrt werden kann.
 */
import { addDays, daysBetween, monthKey } from "./dates.js";
import { normalizeName } from "./normalize.js";
import { openCents, } from "./types.js";
/**
 * Wie weit ein ueberfaelliger Posten in die Zukunft gerollt wird, wenn das
 * gelernte Zahlungsverhalten keinen spaeteren Termin nahelegt.
 *
 * Ein ueberfaelliger Posten darf weder verschwinden (dann fehlt das Geld in der
 * Prognose) noch in der Vergangenheit liegenbleiben (dann waere er sofort
 * faellig und die Kurve springt). Eine Woche ist die ehrlichste Annahme.
 */
const OVERDUE_ROLL_FORWARD_DAYS = 7;
// ---- Erwartungstermin --------------------------------------------------------
/**
 * Wann wir mit dem Geld tatsaechlich rechnen.
 *
 * `expectedDate` am Posten ist der Termin aus dem Quellsystem (Faelligkeit laut
 * Rechnung). Das gelernte Zahlungsverhalten wird bewusst erst hier draufgelegt
 * und nicht schon beim Import — so bleibt der Rohwert nachvollziehbar und die
 * Korrektur laesst sich per Einstellung abschalten.
 */
export function resolveExpectedDate(item, today, profiles = [], applyBehaviour = true) {
    let date = item.expectedDate;
    // Zahlungsverhalten nur auf Forderungen anwenden: wann *wir* zahlen,
    // entscheiden wir selbst — da hilft kein statistischer Mittelwert.
    if (applyBehaviour && item.direction === "in") {
        const profile = findProfile(item, profiles);
        if (profile && profile.sampleCount >= 3 && profile.medianDelayDays !== 0) {
            date = addDays(date, Math.round(profile.medianDelayDays));
        }
    }
    if (date < today) {
        const profile = findProfile(item, profiles);
        const delay = applyBehaviour && profile && profile.sampleCount >= 3 ? Math.round(profile.medianDelayDays) : 0;
        const behavioural = addDays(item.expectedDate, delay);
        date = behavioural > today ? behavioural : addDays(today, OVERDUE_ROLL_FORWARD_DAYS);
    }
    return date;
}
function findProfile(item, profiles) {
    if (profiles.length === 0)
        return null;
    const name = normalizeName(item.counterparty);
    for (const p of profiles) {
        if (item.counterpartyId !== null && p.companyId === item.counterpartyId)
            return p;
    }
    for (const p of profiles) {
        if (normalizeName(p.displayName) === name)
            return p;
        if (p.aliases.some((a) => normalizeName(a) === name))
            return p;
    }
    return null;
}
// ---- Kurve -------------------------------------------------------------------
/**
 * Baut die tagesgenaue Liquiditaetskurve.
 *
 * Vergangenheit wird aus dem heutigen Saldo rueckwaerts rekonstruiert (Saldo an
 * Tag d = heutiger Saldo minus alle Buchungen nach d), Zukunft aus den offenen
 * Restbetraegen vorwaerts fortgeschrieben. Der Uebergang liegt exakt auf `today`
 * — das ist die Ist/Plan-Trennlinie im Chart.
 */
export function buildForecast(input) {
    const { today, openingBalanceCents, plannedItems, bankTransactions = [], profiles = [], to, } = input;
    const applyBehaviour = input.settings?.applyPaymentBehaviour ?? true;
    const from = input.from ?? today;
    const inflow = new Map();
    const outflow = new Map();
    // Vergangenheit: tatsaechliche Buchungen.
    for (const tx of bankTransactions) {
        if (tx.bookingDate > today)
            continue;
        const map = tx.amountCents >= 0 ? inflow : outflow;
        map.set(tx.bookingDate, (map.get(tx.bookingDate) ?? 0) + Math.abs(tx.amountCents));
    }
    // Zukunft: ausschliesslich offene Restbetraege.
    for (const item of plannedItems) {
        const open = openCents(item);
        if (open <= 0)
            continue;
        const date = resolveExpectedDate(item, today, profiles, applyBehaviour);
        if (date > to)
            continue;
        const map = item.direction === "in" ? inflow : outflow;
        map.set(date, (map.get(date) ?? 0) + open);
    }
    // Rueckwaerts vom heutigen Saldo bis zum Beginn des Fensters.
    const past = [];
    let balance = openingBalanceCents;
    for (let d = today; d >= from; d = addDays(d, -1)) {
        const inCents = inflow.get(d) ?? 0;
        const outCents = outflow.get(d) ?? 0;
        past.push({ date: d, balanceCents: balance, inflowCents: inCents, outflowCents: outCents, isActual: true });
        // Der Saldo *vor* diesem Tag ist der heutige minus dessen Bewegungen.
        balance = balance - inCents + outCents;
    }
    past.reverse();
    // Vorwaerts ab morgen.
    const future = [];
    balance = openingBalanceCents;
    for (let d = addDays(today, 1); d <= to; d = addDays(d, 1)) {
        const inCents = inflow.get(d) ?? 0;
        const outCents = outflow.get(d) ?? 0;
        balance += inCents - outCents;
        future.push({ date: d, balanceCents: balance, inflowCents: inCents, outflowCents: outCents, isActual: false });
    }
    return [...past, ...future];
}
export function summarize(points, today, minimumBalanceCents = 0) {
    const byDate = new Map(points.map((p) => [p.date, p]));
    const at = (offset) => {
        const target = addDays(today, offset);
        const exact = byDate.get(target);
        if (exact)
            return exact.balanceCents;
        // Kein Punkt an dem Tag (Horizont kuerzer) → letzten bekannten Stand nehmen.
        const before = points.filter((p) => p.date <= target);
        return before.length > 0 ? before[before.length - 1].balanceCents : 0;
    };
    const future = points.filter((p) => !p.isActual);
    let lowest = null;
    for (const p of future) {
        if (!lowest || p.balanceCents < lowest.balanceCents) {
            lowest = { date: p.date, balanceCents: p.balanceCents };
        }
    }
    const breach = future.find((p) => p.balanceCents < minimumBalanceCents) ?? null;
    const negative = future.find((p) => p.balanceCents < 0) ?? null;
    return {
        balanceTodayCents: at(0),
        balanceIn30Cents: at(30),
        balanceIn60Cents: at(60),
        balanceIn90Cents: at(90),
        lowestPoint: lowest,
        breachDate: breach?.date ?? null,
        runwayDays: negative ? daysBetween(today, negative.date) : null,
    };
}
/**
 * Die Agicap-typische Tabelle Monate x Kategorien.
 *
 * "Ist" ist dabei der bereits durch Bankbuchungen gedeckte Teil eines Postens,
 * "Plan" der noch offene Rest — beide aus derselben Quelle, damit sie sich nie
 * widersprechen koennen.
 */
export function monthlyBreakdown(items, today, profiles = [], applyBehaviour = true) {
    const cells = new Map();
    const touch = (month, category, direction) => {
        const key = `${month}|${category}|${direction}`;
        let cell = cells.get(key);
        if (!cell) {
            cell = { month, category, direction, actualCents: 0, plannedCents: 0 };
            cells.set(key, cell);
        }
        return cell;
    };
    for (const item of items) {
        if (item.status === "cancelled")
            continue;
        if (item.matchedCents > 0) {
            // Der gedeckte Teil gehoert in den Monat, in dem er erwartet wurde.
            touch(monthKey(item.expectedDate), item.category, item.direction).actualCents += item.matchedCents;
        }
        const open = openCents(item);
        if (open > 0) {
            const date = resolveExpectedDate(item, today, profiles, applyBehaviour);
            touch(monthKey(date), item.category, item.direction).plannedCents += open;
        }
    }
    return [...cells.values()].sort((a, b) => a.month.localeCompare(b.month) || a.category.localeCompare(b.category));
}
/**
 * Debitorenalter: wie viel Cash wie lange feststeckt.
 *
 * Bewusst auf dem urspruenglichen Faelligkeitsdatum berechnet, nicht auf dem
 * prognostizierten — ein Kunde, der laut Statistik immer 30 Tage zu spaet zahlt,
 * ist trotzdem 30 Tage zu spaet.
 */
export function agingBuckets(items, today, direction = "in") {
    const buckets = {
        notDue: { label: "notDue", itemCount: 0, openCents: 0 },
        d0_30: { label: "d0_30", itemCount: 0, openCents: 0 },
        d31_60: { label: "d31_60", itemCount: 0, openCents: 0 },
        d61_90: { label: "d61_90", itemCount: 0, openCents: 0 },
        d90plus: { label: "d90plus", itemCount: 0, openCents: 0 },
    };
    for (const item of items) {
        if (item.direction !== direction)
            continue;
        const open = openCents(item);
        if (open <= 0)
            continue;
        const overdueDays = daysBetween(item.originalDate, today);
        const bucket = overdueDays <= 0 ? buckets.notDue
            : overdueDays <= 30 ? buckets.d0_30
                : overdueDays <= 60 ? buckets.d31_60
                    : overdueDays <= 90 ? buckets.d61_90
                        : buckets.d90plus;
        bucket.itemCount++;
        bucket.openCents += open;
    }
    return Object.values(buckets);
}
// ---- Restkosten-Puffer -------------------------------------------------------
/**
 * Schaetzt laufende Ausgaben, die weder Rechnung noch Fixkosten sind.
 *
 * Ohne diesen Posten ist jede Prognose systematisch zu optimistisch: es gibt
 * immer Kartenzahlungen, Gebuehren und Kleinkram, die nirgends geplant sind.
 * Grundlage ist der Durchschnitt der ungematchten Abgaenge der letzten Monate.
 */
export function residualBufferPerMonth(transactions, today, lookbackMonths) {
    const start = addDays(today, -lookbackMonths * 30);
    const monthly = new Map();
    for (const tx of transactions) {
        if (tx.amountCents >= 0)
            continue;
        if (tx.bookingDate < start || tx.bookingDate > today)
            continue;
        // Nur was keiner Planung zugeordnet werden konnte — alles andere ist
        // bereits als Posten erfasst und wuerde hier doppelt gezaehlt.
        if (tx.matchState !== "unmatched")
            continue;
        const m = monthKey(tx.bookingDate);
        monthly.set(m, (monthly.get(m) ?? 0) + Math.abs(tx.amountCents));
    }
    if (monthly.size === 0)
        return 0;
    const total = [...monthly.values()].reduce((a, b) => a + b, 0);
    return Math.round(total / monthly.size);
}
// ---- Szenarien ---------------------------------------------------------------
/**
 * Legt ein Szenario als Overlay auf die Posten.
 *
 * Szenarien veraendern nie den Datenbestand — sie liefern eine transformierte
 * Kopie. Deshalb ist der Wechsel zwischen Szenarien im UI verlustfrei und die
 * Baseline bleibt immer erreichbar.
 */
export function applyScenario(items, scenario) {
    if (!scenario || scenario.isBaseline || scenario.adjustments.length === 0) {
        return [...items];
    }
    let result = items.map((i) => ({ ...i }));
    for (const adj of scenario.adjustments) {
        result = applyAdjustment(result, adj);
    }
    return result;
}
function applyAdjustment(items, adj) {
    switch (adj.kind) {
        case "disableItem":
            return items.filter((i) => i.id !== adj.plannedItemId);
        case "shiftItem":
            return items.map((i) => i.id === adj.plannedItemId ? { ...i, expectedDate: addDays(i.expectedDate, adj.days ?? 0) } : i);
        case "scaleItem":
            return items.map((i) => (i.id === adj.plannedItemId ? scaleItem(i, adj.percent ?? 100) : i));
        case "globalDelay":
            // Nur Forderungen: eine Annahme ueber Zahlungsverzug betrifft unsere
            // Kunden, nicht unsere eigenen Ueberweisungen.
            return items.map((i) => i.direction === "in" ? { ...i, expectedDate: addDays(i.expectedDate, adj.days ?? 0) } : i);
        case "globalRevenueScale":
            return items.map((i) => (i.direction === "in" ? scaleItem(i, adj.percent ?? 100) : i));
        case "addItem": {
            if (!adj.item)
                return items;
            return [...items, materializeScenarioItem(adj.item)];
        }
        default:
            return items;
    }
}
/**
 * Skaliert einen Posten.
 *
 * Der bereits gematchte Teil bleibt unangetastet — was gezahlt ist, ist gezahlt.
 * Skaliert wird nur der offene Rest, sonst wuerde ein Szenario die Vergangenheit
 * umschreiben.
 */
function scaleItem(item, percent) {
    const factor = percent / 100;
    const open = openCents(item);
    const scaledOpen = Math.round(open * factor);
    return { ...item, grossCents: item.matchedCents + scaledOpen };
}
function materializeScenarioItem(partial) {
    const now = new Date().toISOString();
    return {
        id: partial.id ?? `scenario-${partial.title}-${partial.expectedDate}`,
        source: "manual",
        sourceId: partial.sourceId ?? `scenario:${partial.title}`,
        direction: partial.direction,
        grossCents: partial.grossCents,
        netCents: partial.netCents ?? partial.grossCents,
        vatCents: partial.vatCents ?? 0,
        vatRatePercent: partial.vatRatePercent ?? 0,
        reverseCharge: partial.reverseCharge ?? false,
        expectedDate: partial.expectedDate,
        originalDate: partial.originalDate ?? partial.expectedDate,
        paymentDate: null,
        documentDate: partial.documentDate ?? partial.expectedDate,
        counterparty: partial.counterparty ?? "Szenario",
        counterpartyId: partial.counterpartyId ?? null,
        counterpartyIban: partial.counterpartyIban ?? null,
        reference: partial.reference ?? null,
        category: partial.category ?? "other",
        status: "open",
        matchedCents: 0,
        mocoPaidCents: 0,
        currency: partial.currency ?? "EUR",
        title: partial.title,
        updatedAt: now,
    };
}
//# sourceMappingURL=forecast.js.map